import { useCallback, useEffect, useRef, useState } from 'react'
import type { ComparisonResult, HeatmapCell, SimulationInputs, SimulationResult } from '../types'

interface JobSpec {
  simulate: { payload: { inputs: SimulationInputs }; data: SimulationResult }
  compare: { payload: { strategies: { label: string; inputs: SimulationInputs }[] }; data: ComparisonResult[] }
  heatmap: { payload: { inputs: SimulationInputs; ivs: number[]; rvs: number[] }; data: HeatmapCell[] }
}

export type JobKind = keyof JobSpec

const RESULT_TYPE: Record<JobKind, string> = { simulate: 'result', compare: 'comparison', heatmap: 'heatmap' }

function extract<K extends JobKind>(kind: K, message: Record<string, unknown>): JobSpec[K]['data'] {
  if (kind === 'simulate') return message.result as JobSpec[K]['data']
  if (kind === 'compare') return message.results as JobSpec[K]['data']
  return message.cells as JobSpec[K]['data']
}

/**
 * Runs one kind of simulation job in its own Web Worker. Starting a new job or
 * cancelling terminates the previous worker, so CPU work stops immediately and
 * different job kinds never queue behind each other.
 *
 * `context` is stored alongside the completed data (e.g. the inputs a batch was
 * run with) so callers can tell when a displayed result has gone stale.
 */
export function useSimulationJob<K extends JobKind, C = undefined>(kind: K) {
  const [data, setData] = useState<{ value: JobSpec[K]['data']; context: C } | null>(null)
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')
  const workerRef = useRef<Worker | null>(null)
  const requestId = useRef(0)

  const stop = useCallback(() => {
    requestId.current += 1
    workerRef.current?.terminate()
    workerRef.current = null
  }, [])

  useEffect(() => stop, [stop])

  const cancel = useCallback(() => {
    stop()
    setRunning(false)
    setProgress(0)
  }, [stop])

  const run = useCallback((payload: JobSpec[K]['payload'], context: C) => {
    stop()
    const id = requestId.current
    const worker = new Worker(new URL('../workers/simulation.worker.ts', import.meta.url), { type: 'module' })
    workerRef.current = worker
    setError('')
    setRunning(true)
    setProgress(0)

    const isCurrent = () => requestId.current === id && workerRef.current === worker
    const finish = () => {
      worker.terminate()
      if (workerRef.current === worker) workerRef.current = null
      setRunning(false)
    }
    worker.onmessage = ({ data: message }: MessageEvent<Record<string, unknown>>) => {
      if (!isCurrent() || message.id !== id) return
      if (message.type === 'progress') {
        setProgress(Number(message.progress))
      } else if (message.type === RESULT_TYPE[kind]) {
        setData({ value: extract(kind, message), context })
        setProgress(100)
        finish()
      } else if (message.type === 'error') {
        setError(String(message.message))
        finish()
      }
    }
    worker.onerror = (event) => {
      if (!isCurrent()) return
      setError(event.message || '计算线程异常，请重试。')
      finish()
    }
    worker.postMessage({ type: kind, id, ...payload })
  }, [kind, stop])

  const clear = useCallback(() => setData(null), [])
  const dismissError = useCallback(() => setError(''), [])

  return { data, running, progress, error, run, cancel, clear, dismissError }
}
